import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  DEFAULT_STYLE_SETTINGS,
  STYLE_FONT_SCALE,
  STYLE_SETTINGS_STORAGE_KEY,
  STYLE_SHADOW_PRESETS,
  STYLE_SHADOW_PRESETS_DARK,
  StyleSettingsConfig,
  StyleTheme,
} from '../models/style-settings.model';
import { UiService } from './ui.service';

@Injectable({
  providedIn: 'root',
})
export class StyleSettingsService {
  private readonly ui = inject(UiService);
  private readonly configSubject = new BehaviorSubject<StyleSettingsConfig>(
    this.getDefaults()
  );

  /** Emits whenever theme/style config is applied (header toggle, Style Settings, boot). */
  readonly config$ = this.configSubject.asObservable();

  getDefaults(): StyleSettingsConfig {
    return { ...DEFAULT_STYLE_SETTINGS };
  }

  getSnapshot(): StyleSettingsConfig {
    return { ...this.configSubject.value };
  }

  load(): StyleSettingsConfig {
    try {
      const raw = localStorage.getItem(STYLE_SETTINGS_STORAGE_KEY);
      if (!raw) {
        return this.getDefaults();
      }
      const parsed = JSON.parse(raw) as Partial<StyleSettingsConfig>;
      return this.normalize({ ...this.getDefaults(), ...parsed });
    } catch {
      return this.getDefaults();
    }
  }

  save(config: StyleSettingsConfig): StyleSettingsConfig {
    const normalized = this.normalize(config);
    localStorage.setItem(STYLE_SETTINGS_STORAGE_KEY, JSON.stringify(normalized));
    this.apply(normalized);
    return normalized;
  }

  /** Flip light ↔ dark and persist (header toggle). */
  toggleTheme(): StyleSettingsConfig {
    const next = this.load();
    next.theme = next.theme === 'dark' ? 'light' : 'dark';
    return this.save(next);
  }

  setTheme(theme: StyleTheme): StyleSettingsConfig {
    const next = this.load();
    next.theme = theme;
    return this.save(next);
  }

  reset(): StyleSettingsConfig {
    const defaults = this.getDefaults();
    localStorage.removeItem(STYLE_SETTINGS_STORAGE_KEY);
    this.apply(defaults);
    return defaults;
  }

  loadAndApply(): StyleSettingsConfig {
    const config = this.load();
    this.apply(config);
    return config;
  }

  apply(config: StyleSettingsConfig): void {
    const normalized = this.normalize(config);
    const root = document.documentElement;
    const body = document.body;

    root.setAttribute('data-ds-theme', normalized.theme);
    root.style.colorScheme = normalized.theme === 'dark' ? 'dark' : 'light';

    // Bridge legacy Skote / Bootstrap dark theme (`_theme-dark.scss`)
    if (body) {
      body.setAttribute('data-layout-mode', normalized.theme);
    }

    const shadowPresets =
      normalized.theme === 'dark' ? STYLE_SHADOW_PRESETS_DARK : STYLE_SHADOW_PRESETS;

    this.ui.cssVar({
      root,
      cssPrefix: 'ds',
      property: 'radius-card',
      value: `${normalized.cardRadius}px`,
    });
    this.ui.cssVar({
      root,
      cssPrefix: 'ds',
      property: 'radius-button',
      value: `${normalized.buttonRadius}px`,
    });
    this.ui.cssVar({
      root,
      cssPrefix: 'ds',
      property: 'card-border-width',
      value: normalized.cardBorder ? '1px' : '0',
    });
    this.ui.cssVar({
      root,
      cssPrefix: 'ds',
      property: 'font-scale',
      value: String(STYLE_FONT_SCALE[normalized.fontSize]),
    });
    this.ui.cssVar({
      root,
      cssPrefix: 'ds',
      property: 'shadow-card',
      value: shadowPresets[normalized.shadow],
    });

    this.configSubject.next(normalized);
  }

  private normalize(config: StyleSettingsConfig): StyleSettingsConfig {
    return {
      cardRadius: this.clamp(Math.round(Number(config.cardRadius) || 0), 0, 24),
      buttonRadius: this.clamp(Math.round(Number(config.buttonRadius) || 0), 0, 24),
      cardBorder: !!config.cardBorder,
      theme: this.normalizeTheme(config.theme as string),
      fontSize: ['sm', 'md', 'lg'].includes(config.fontSize) ? config.fontSize : 'md',
      shadow: ['none', 'soft', 'medium', 'strong'].includes(config.shadow)
        ? config.shadow
        : 'soft',
    };
  }

  /** Map legacy saved values (`default`, `bw`) to light/dark. */
  private normalizeTheme(theme: string): StyleTheme {
    if (theme === 'dark') {
      return 'dark';
    }
    return 'light';
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }
}
